'use client';

import { BigNum, QUOTE_PRECISION_EXP, ZERO } from '@velocity-exchange/sdk';
import { useMemo, useState } from 'react';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import { useCurrentAccountMarginInfo } from 'src/hooks/query/accountTradingData/useMarginInfo';
import { usePerpMarketsSortedByMarketIndex } from 'src/hooks/perpMarkets/usePerpMarketsSortedByMarketIndex';
import SettleSelect from 'src/components/Inputs/SettleSelect';
import Tooltip from 'src/components/primitives/Tooltip/Tooltip';
import DefinitionTooltip from 'src/components/Tooltip/DefinitionTooltip/DefinitionTooltip';
import { AlertTriangle } from '@velocity-exchange/icons';
import ShowHideBalanceButton from '../ShowHideAccountValuesButton/ShowHideAccountValuesButton';
import useShowAccountValues from 'src/hooks/useShowAccountValues';
import Button from '../primitives/Button/Button';
import MobileTableCard from 'src/components/Tables/cards/MobileTableCard';
import { getColorVar } from 'src/utils/theme/colorVars';
import { cj } from 'src/utils/classNames';
import useSettleAllPnls from 'src/hooks/drift-actions/useSettlePnls';
import useDevSwitchIsOn from 'src/hooks/useDevSwitchIsOn';

interface Props {
	netValue: BigNum;
}

const BalanceTableSummary = ({ netValue }: Props) => {
	const isMobile = useIsMobileScreenSize();
	const [submitting, setSubmitting] = useState(false);
	const perpMarketsSortedByMarketIndex = usePerpMarketsSortedByMarketIndex();
	const marginInfo = useCurrentAccountMarginInfo();
	const totalUnsettledPnl = marginInfo?.totalUnsettledPnl;
	const marketsNeedingSettlement = marginInfo?.marketsNeedingSettlement;
	const showAccountValues = useShowAccountValues();
	const marketsNeedingSettlementLength = marketsNeedingSettlement?.length ?? 0;
	const cantSettle = marketsNeedingSettlementLength > 0;
	const unsettledBalance = BigNum.from(
		totalUnsettledPnl || ZERO,
		QUOTE_PRECISION_EXP
	);
	const { settleAllPnls } = useSettleAllPnls();
	const isDevMode = useDevSwitchIsOn();

	const showSettlePoolAction = cantSettle && isDevMode;

	const cantSettleTooltipText = useMemo(() => {
		if (!showSettlePoolAction) return undefined;

		const marketSymbols = (marketsNeedingSettlement ?? []).map(
			(marketIndex) => perpMarketsSortedByMarketIndex[marketIndex]?.symbol
		);

		const label =
			marketSymbols.length > 1
				? marketSymbols.join(', ').trimEnd()
				: (marketSymbols[0] ?? '');

		return `${label} market(s) can't be settled now because there's not enough liquidity in the P&L pool. Please try to settle the negative P&L balances in the pool first.`;
	}, [
		showSettlePoolAction,
		marketsNeedingSettlement,
		perpMarketsSortedByMarketIndex,
	]);

	const handleSettle = async () => {
		setSubmitting(true);
		try {
			await settleAllPnls();
		} finally {
			setSubmitting(false);
		}
	};

	if (isMobile) {
		return (
			<>
				<MobileTableCard.Root className="w-full flex-col gap-y-xs">
					<MobileTableCard.Label>Net Value</MobileTableCard.Label>
					<span className="flex items-center gap-x-xs typo-label-md text-text-default">
						{showAccountValues ? netValue.toNotional() : '*****'}
						<ShowHideBalanceButton />
					</span>
				</MobileTableCard.Root>

				<MobileTableCard.Root className="flex-row items-center justify-between">
					<div className="flex flex-col gap-y-xs">
						<MobileTableCard.Label>Unsettled</MobileTableCard.Label>
						<div className="flex items-center gap-x-xs">
							<span
								className={cj(
									'typo-label-md text-text-default',
									showSettlePoolAction && 'text-text-secondary'
								)}
							>
								{showAccountValues ? unsettledBalance.toNotional() : '*****'}
							</span>
							{showSettlePoolAction && (
								<Tooltip content={cantSettleTooltipText}>
									<AlertTriangle
										className="size-5"
										color={getColorVar('text-secondary')}
									/>
								</Tooltip>
							)}
						</div>
					</div>
					<div className="inline-flex items-center gap-x-sm">
						<Button
							size="sm"
							variant="secondary"
							onClick={handleSettle}
							disabled={submitting || unsettledBalance.eqZero() || cantSettle}
							isLoading={submitting}
						>
							Settle
						</Button>
						{showSettlePoolAction && (
							<SettleSelect
								options={
									marketsNeedingSettlement?.map(
										(mktIndex) => perpMarketsSortedByMarketIndex[mktIndex]
									) ?? []
								}
							/>
						)}
					</div>
				</MobileTableCard.Root>
			</>
		);
	}

	return (
		<div className="flex items-center gap-x-4xl border-b border-stroke-divider bg-container-bg px-md py-sm">
			<div className="flex flex-col gap-y-xs">
				<span className="flex items-center gap-x-xs typo-body-xs text-text-secondary">
					Net Value
					<ShowHideBalanceButton className="-m-sm p-sm" />
				</span>
				<span className="typo-label-md text-text-default">
					{showAccountValues ? netValue.toNotional() : '*****'}
				</span>
			</div>
			<div className="flex items-center gap-x-xl typo-body-xs">
				<div className="flex flex-col gap-y-xs">
					<DefinitionTooltip
						definitionId="unsettledBalance"
						className="w-fit text-text-secondary"
						side="right"
					>
						Unsettled
					</DefinitionTooltip>
					<div className="flex items-center gap-x-lg typo-label-md text-text-default">
						{showAccountValues ? unsettledBalance.toNotional() : '*****'}
					</div>
				</div>
				<Button
					size="sm"
					variant="stroke"
					onClick={handleSettle}
					disabled={submitting || unsettledBalance.eqZero()}
					isLoading={submitting}
				>
					Settle
				</Button>
				{showSettlePoolAction && (
					<SettleSelect
						options={
							marketsNeedingSettlement?.map(
								(mktIndex) => perpMarketsSortedByMarketIndex[mktIndex]
							) ?? []
						}
					/>
				)}
			</div>
		</div>
	);
};

export default BalanceTableSummary;
